"use client";

import React, { createContext, useContext, useMemo } from "react";
import type { ReactNode, CSSProperties } from "react";

import type { SiteConfig } from "@/types/sites";
import { useSiteConfigSafe } from "@/contexts/SiteConfigContext";
import {
  useSectionRenderInfo,
  type SectionRenderInfo,
} from "@/components/landingPage/common/SectionRenderContext";
import {
  getCardBackgroundForSection,
  getCardTextColorForSection,
} from "@/components/landingPage/common/backgroundUtils";
import { getPaletteClass } from "@/utils/paletteUtils";
import { getPaletteStyle } from "@/utils/paletteUtils";
import { cn } from "@/lib/utils";

export type LandingUICardVariant = "default" | "featured" | "elevated";

export interface LandingUICardOptions {
  variant?: LandingUICardVariant;
}

export interface LandingUICardTokens {
  /** Classes applied to the outer card container */
  getContainerClassName: (options?: LandingUICardOptions) => string | undefined;
  /** Theme-aware text colors for card content */
  getTextClassName: (
    tone?: "primary" | "secondary" | "muted",
    options?: LandingUICardOptions
  ) => string | undefined;
  /** Theme-aware border color for card */
  getBorderClassName: () => string | undefined;
  /** Get radial gradient classes based on theme palette */
  getRadialGradientClassName: () => string | undefined;
}

export type LandingUITabsVariant = "underline" | "filled";

export interface LandingUITabsTokens {
  getListClassName: (variant?: LandingUITabsVariant) => string | undefined;
  getTriggerBaseClassName: (
    variant?: LandingUITabsVariant
  ) => string | undefined;
  getTriggerActiveClassName: (
    variant?: LandingUITabsVariant
  ) => string | undefined;
  getTriggerActiveStyle: (
    variant?: LandingUITabsVariant
  ) => CSSProperties | undefined;
}

export interface LandingUIProgressTokens {
  getTrackClassName: () => string | undefined;
  getIndicatorClassName: () => string | undefined;
}

export interface LandingUITokens {
  card: LandingUICardTokens;
  tabs: LandingUITabsTokens;
  progress: LandingUIProgressTokens;
}

const LandingUIContext = createContext<LandingUITokens | null>(null);

const DEFAULT_CARD_TOKENS: LandingUICardTokens = {
  getContainerClassName: () => undefined,
  getTextClassName: () => undefined,
  getBorderClassName: () => undefined,
  getRadialGradientClassName: () => undefined,
};

const DEFAULT_TABS_TOKENS: LandingUITabsTokens = {
  getListClassName: () => undefined,
  getTriggerBaseClassName: () => undefined,
  getTriggerActiveClassName: () => undefined,
  getTriggerActiveStyle: () => undefined,
};

const DEFAULT_PROGRESS_TOKENS: LandingUIProgressTokens = {
  getTrackClassName: () => undefined,
  getIndicatorClassName: () => undefined,
};

const mapCardVariant = (
  variant: LandingUICardVariant | undefined
): "default" | "featured" | "elevated" => {
  switch (variant) {
    case "featured":
      return "featured";
    case "elevated":
      return "elevated";
    default:
      return "default";
  }
};

/**
 * Get radial gradient classes based on theme palette color family
 * Tailwind requires complete class names at build time
 */
const getRadialGradientClasses = (paletteName?: string): string => {
  // Extract color family from palette name (e.g., "tailwindRose" -> "rose")
  const colorFamily =
    paletteName?.replace(/^tailwind/, "").toLowerCase() || "blue";

  // Map color families to gradient classes
  const gradientMap: Record<string, string> = {
    amber: "from-amber-600/35 dark:from-amber-500/35",
    blue: "from-blue-600/35 dark:from-blue-500/35",
    cyan: "from-cyan-600/35 dark:from-cyan-500/35",
    emerald: "from-emerald-600/35 dark:from-emerald-500/35",
    fuchsia: "from-fuchsia-600/35 dark:from-fuchsia-500/35",
    gray: "from-gray-600/35 dark:from-gray-500/35",
    green: "from-green-600/35 dark:from-green-500/35",
    indigo: "from-indigo-600/35 dark:from-indigo-500/35",
    lime: "from-lime-600/35 dark:from-lime-500/35",
    neutral: "from-neutral-600/35 dark:from-neutral-500/35",
    orange: "from-orange-600/35 dark:from-orange-500/35",
    pink: "from-pink-600/35 dark:from-pink-500/35",
    purple: "from-purple-600/35 dark:from-purple-500/35",
    red: "from-red-600/35 dark:from-red-500/35",
    rose: "from-rose-600/35 dark:from-rose-500/35",
    sky: "from-sky-600/35 dark:from-sky-500/35",
    slate: "from-slate-600/35 dark:from-slate-500/35",
    stone: "from-stone-600/35 dark:from-stone-500/35",
    teal: "from-teal-600/35 dark:from-teal-500/35",
    violet: "from-violet-600/35 dark:from-violet-500/35",
    yellow: "from-yellow-600/35 dark:from-yellow-500/35",
    zinc: "from-zinc-600/35 dark:from-zinc-500/35",
  };

  return gradientMap[colorFamily] || gradientMap.blue;
};

/**
 * Computes a border color based on the card's background color.
 * Logic: If the background is white or a light gray shade (e.g., gray-50),
 * return a border that's ~200-300 tones darker in the gray family.
 *
 * @param cardBg - The card background class string (e.g., "bg-white dark:bg-gray-900")
 * @returns A border color class that's derived from the card background
 */
const computeBorderFromCardBackground = (
  cardBg: string | undefined
): string => {
  if (!cardBg) {
    return "border-gray-300 dark:border-gray-800";
  }

  // Parse the light mode background color
  // Examples: "bg-white", "bg-gray-50", "bg-gray-100"
  const bgMatch = cardBg.match(
    /bg-(white|gray-(\d+)|slate-(\d+)|neutral-(\d+)|zinc-(\d+)|stone-(\d+))/
  );

  if (!bgMatch) {
    // If we can't parse it, return a sensible default
    return "border-gray-300 dark:border-gray-800";
  }

  const fullMatch = bgMatch[1]; // e.g., "white", "gray-50", "slate-100"

  // Handle white background
  if (fullMatch === "white") {
    return "border-gray-300 dark:border-gray-800";
  }

  // Extract tone from the background color
  // For "gray-50", we get tone="50"
  const parts = fullMatch.split("-");
  const baseTone = parts[1] ? parseInt(parts[1], 10) : 50;

  // Compute border tone: base + 200-250
  // If bg is gray-50, border should be gray-300
  // If bg is gray-100, border should be gray-300 or gray-400
  let borderTone = baseTone + 250;

  // Clamp to valid Tailwind tones: 50, 100, 200, 300, 400, 500, 600, 700, 800, 900
  // For borders on light backgrounds, we want 200-400 range
  if (borderTone < 200) borderTone = 200;
  if (borderTone > 400) borderTone = 400;

  // Round to nearest valid Tailwind tone
  const validTones = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];
  borderTone = validTones.reduce((prev, curr) =>
    Math.abs(curr - borderTone) < Math.abs(prev - borderTone) ? curr : prev
  );
   // Use gray family for border regardless of card background family
  // This ensures consistency across all themes
  return `border-gray-${borderTone} dark:border-gray-800`;
};

const createCardTokens = (
  config: SiteConfig | undefined,
  sectionInfo: SectionRenderInfo | null
): LandingUICardTokens => {
  if (!config) {
    return DEFAULT_CARD_TOKENS;
  }

  const sectionName = sectionInfo?.sectionName ?? "global";
  const sectionIndex = sectionInfo?.sectionIndex;

  return {
    getContainerClassName: (options) => {
      const variant = mapCardVariant(options?.variant);
      return getCardBackgroundForSection(
        config,
        sectionName,
        variant,
        sectionIndex
      );
    },
    getTextClassName: (tone = "primary") => {
      return getCardTextColorForSection(
        config,
        sectionName,
        tone,
        sectionIndex
      );
    },
    getBorderClassName: () => {
      // Get the card's actual background color
      const cardBg = getCardBackgroundForSection(
        config,
        sectionName,
        "default",
        sectionIndex
      );

      // Compute border color from the card background
      return computeBorderFromCardBackground(cardBg);
    },
    getRadialGradientClassName: () => {
      return getRadialGradientClasses(config.theme?.paletteName);
    },
  };
};

const createProgressTokens = (
  config: SiteConfig | undefined
): LandingUIProgressTokens => {
  if (!config) {
    return DEFAULT_PROGRESS_TOKENS;
  }

  const { theme } = config;

  const trackBackground = getPaletteClass(
    theme,
    "backgroundSoft",
    "bg-primary/20"
  );
  const indicatorAccent = getPaletteClass(
    theme,
    "backgroundAccentColor",
    "bg-primary"
  );
  const indicatorGradient = getPaletteClass(theme, "gradientPrimary", "");

  return {
    getTrackClassName: () => trackBackground,
    getIndicatorClassName: () =>
      cn(
        indicatorGradient && indicatorGradient.trim().length > 0
          ? indicatorGradient
          : undefined,
        indicatorAccent
      ),
  };
};

const applyVariantToClasses = (
  classNames: string | undefined,
  variant: string
): string | undefined => {
  if (!classNames) return undefined;
  return classNames
    .split(" ")
    .filter(Boolean)
    .map((token) => {
      const segments = token.split(":");
      const base = segments.pop();
      if (!base) return token;
      const variants = [...segments, variant];
      return `${variants.join(":")}:${base}`;
    })
    .join(" ");
};

const createTabsTokens = (
  config: SiteConfig | undefined
): LandingUITabsTokens => {
  if (!config) {
    return DEFAULT_TABS_TOKENS;
  }

  const { theme } = config;
  const listSurface = "bg-transparent";
  // const listSurface = getPaletteClass(
  //   theme,
  //   "backgroundPrimary",
  //   "bg-white dark:bg-slate-900"
  // );
  const listText = getPaletteClass(
    theme,
    "textSecondary",
    "text-gray-500 dark:text-gray-400"
  );
  const listBorder = "border-gray-200 dark:border-gray-800";

  const triggerBaseText = getPaletteClass(
    theme,
    "textSecondary",
    "text-gray-500 dark:text-gray-400"
  );
  const triggerActiveText = getPaletteClass(
    theme,
    "textPrimary",
    "text-gray-900 dark:text-white"
  );
  const triggerActiveBorder = getPaletteClass(
    theme,
    "borderPrimary",
    "border-blue-500 dark:border-blue-400"
  );
  const triggerActiveBackground = getPaletteClass(
    theme,
    "backgroundPrimaryColor",
    "bg-primary"
  );
  const triggerActiveFilledText = getPaletteClass(
    theme,
    "textOnAccent",
    "text-primary-foreground"
  );
  const hoverBackground = applyVariantToClasses(
    getPaletteClass(theme, "backgroundSecondary", "bg-muted"),
    "hover"
  );
  const hoverText = applyVariantToClasses(triggerActiveText, "hover");
  const hoverBorder = applyVariantToClasses(listBorder, "hover");

  const activeBorderStyle = getPaletteStyle(theme, "borderPrimary") as
    | CSSProperties
    | undefined;

  const borderColorValue =
    typeof activeBorderStyle?.borderColor === "string"
      ? activeBorderStyle.borderColor
      : undefined;

  // Get the background color for filled tabs active state
  const activeBackgroundStyle = getPaletteStyle(theme, "backgroundPrimaryColor") as
    | CSSProperties
    | undefined;

  const backgroundColorValue =
    typeof activeBackgroundStyle?.backgroundColor === "string"
      ? activeBackgroundStyle.backgroundColor
      : undefined;

  const variantConfigs: Record<
    LandingUITabsVariant,
    {
      list?: string;
      base?: string;
      active?: string;
      style?: CSSProperties;
    }
  > = {
    underline: {
      list: cn(listSurface, listBorder, listText),
      base: triggerBaseText,
      active: cn(
        applyVariantToClasses(triggerActiveText, "data-[state=active]"),
        applyVariantToClasses(triggerActiveBorder, "data-[state=active]")
      ),
      style: borderColorValue
        ? ({
            "--landing-tabs-active-border": borderColorValue,
          } as CSSProperties)
        : undefined,
    },
    filled: {
      list: cn(listSurface, listText),
      base: cn(triggerBaseText, hoverText, hoverBackground, hoverBorder),
      active: cn(
        applyVariantToClasses(triggerActiveBackground, "data-[state=active]"),
        applyVariantToClasses(triggerActiveFilledText, "data-[state=active]")
      ),
      style: backgroundColorValue
        ? ({
            "--tabs-active-bg": backgroundColorValue,
          } as CSSProperties)
        : undefined,
    },
  };

  return {
    getListClassName: (variant = "underline") => variantConfigs[variant]?.list,
    getTriggerBaseClassName: (variant = "underline") =>
      variantConfigs[variant]?.base,
    getTriggerActiveClassName: (variant = "underline") =>
      variantConfigs[variant]?.active,
    getTriggerActiveStyle: (variant = "underline") =>
      variantConfigs[variant]?.style,
  };
};

const createLandingUITokens = (
  config: SiteConfig | undefined,
  sectionInfo: SectionRenderInfo | null
): LandingUITokens => {
  return {
    card: createCardTokens(config, sectionInfo),
    tabs: createTabsTokens(config),
    progress: createProgressTokens(config),
  };
};

export interface LandingUIProviderProps {
  children: ReactNode;
  configOverride?: SiteConfig;
}

export const LandingUIProvider: React.FC<LandingUIProviderProps> = ({
  children,
  configOverride,
}) => {
  const siteConfig = useSiteConfigSafe();
  const sectionInfo = useSectionRenderInfo();

  const tokens = useMemo(() => {
    const resolvedConfig = configOverride ?? siteConfig?.config;
    return createLandingUITokens(resolvedConfig, sectionInfo);
  }, [sectionInfo, siteConfig?.config, configOverride]);

  return (
    <LandingUIContext.Provider value={tokens}>
      {children}
    </LandingUIContext.Provider>
  );
};

export const useLandingUITokens = () => {
  return useContext(LandingUIContext);
};

export const useLandingUICardTokens = (): LandingUICardTokens => {
  return useLandingUITokens()?.card ?? DEFAULT_CARD_TOKENS;
};

export const useLandingUITabsTokens = (): LandingUITabsTokens => {
  return useLandingUITokens()?.tabs ?? DEFAULT_TABS_TOKENS;
};

export const useLandingUIProgressTokens = (): LandingUIProgressTokens => {
  return useLandingUITokens()?.progress ?? DEFAULT_PROGRESS_TOKENS;
};

export default LandingUIContext;
